<template>
  <div v-if="showBanner" class="as-plan-banner" role="status">
    <div class="as-plan-banner__content">
      <div class="as-plan-banner__text">
        <div class="as-plan-banner__title-row">
          <v-icon color="var(--color-pallet-4)" small class="as-plan-banner__icon">mdi-account-credit-card</v-icon>
          <span class="as-plan-banner__title">{{ $t('dashboard.organization.plan-banner.title') }}</span>
        </div>
        <span class="as-plan-banner__subtitle">{{ $t('dashboard.organization.plan-banner.subtitle') }}</span>
      </div>

      <div class="as-plan-banner__actions">
        <v-btn color="success" depressed class="as-plan-banner__cta" :to="planPageRoute">
          {{ $t('dashboard.organization.plan-banner.choose-plan') }}
        </v-btn>
        <v-btn icon class="as-plan-banner__dismiss" :aria-label="$t('common.button.close')" @click="dismiss">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { MyPagesRouteName } from '@/router/myPagesPaths'
import { SESSION_KEY_PLAN_BANNER_DISMISSED } from '@/js/constants/storageKeys'

/**
 * Shown on every trainer-portal page while the active organization has no `payment_plan`.
 *
 * Visible only to users who can actually choose a plan (OWNER/ADMIN); a trainer cannot act on it.
 * Hidden on the Konts page itself, where the plan selector is. Dismiss is per browser
 * session and per organization, so the banner returns on the next login until a plan is chosen.
 * Scenario: docs/features/settings/scenarios.md S6.
 */
export default {
  name: 'PlanSelectionBanner',
  data() {
    return {
      dismissed: false
    }
  },
  computed: {
    activeOrganization() {
      return this.$store.getters['dashboard/activeOrganization']
    },
    activeOrganizationId() {
      return this.$store.getters['dashboard/activeOrganizationId']
    },
    isAccountTypeOrganization() {
      return this.$store.getters['dashboard/isAccountTypeOrganization']
    },
    canConfigureOrganization() {
      return this.$store.getters['user/canConfigureOrganization'](this.activeOrganizationId)
    },
    hasPlan() {
      return Boolean(this.activeOrganization && this.activeOrganization.payment_plan)
    },
    isOnAccountPage() {
      return this.$route.name === MyPagesRouteName.SETTINGS_ACCOUNT
    },
    showBanner() {
      return this.isAccountTypeOrganization
        && Boolean(this.activeOrganization)
        && !this.hasPlan
        && this.canConfigureOrganization
        && !this.isOnAccountPage
        && !this.dismissed
    },
    planPageRoute() {
      return { name: MyPagesRouteName.SETTINGS_ACCOUNT }
    },
    dismissStorageKey() {
      return `${SESSION_KEY_PLAN_BANNER_DISMISSED}-${this.activeOrganizationId}`
    }
  },
  watch: {
    activeOrganizationId: {
      handler() {
        this.dismissed = sessionStorage.getItem(this.dismissStorageKey) === 'true'
      },
      immediate: true
    }
  },
  methods: {
    dismiss() {
      sessionStorage.setItem(this.dismissStorageKey, 'true')
      this.dismissed = true
    }
  }
}
</script>

<style>
.as-plan-banner {
  background: var(--color-white);
  border: 1px solid var(--color-pallet-4);
  border-left-width: 4px;
  border-radius: 8px;
  margin-bottom: 16px;
}

.as-plan-banner__content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  min-height: 48px;
}

.as-plan-banner__text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.as-plan-banner__title-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.as-plan-banner__title {
  font-size: 15px;
  font-weight: 600;
  color: var(--color-pallet-5);
  line-height: 1.3;
}

.as-plan-banner__subtitle {
  font-size: 13px;
  color: var(--color-gray-1);
  line-height: 1.4;
}

.as-plan-banner__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.as-plan-banner .as-plan-banner__cta {
  text-transform: none;
  font-weight: 600;
}

@media (max-width: 600px) {
  .as-plan-banner__content {
    flex-direction: column;
    align-items: stretch;
    padding: 12px;
  }

  .as-plan-banner__actions {
    justify-content: space-between;
  }

  .as-plan-banner .as-plan-banner__cta {
    flex: 1;
  }
}
</style>
